import { get } from "svelte/store";
import { sessionStore, setSocialGiftBundleItems } from "../../stores/session";
import type { MenuItem } from "./menuItems";
import isEqual from "lodash/isEqual";
import keyBy from "lodash/keyBy";
import type { AvailableMenuItem } from "../../../types/campaign";

export const setupSelectedItems = (menuItems: MenuItem[]) => {
  // If the menu changes, reset the selected items
  const currentSelectedItems = isEqual(menuItems, get(sessionStore).socialGiftBundleMenuItems)
    ? get(sessionStore).socialGiftBundleSelectedItems || []
    : [];

  const selectedItems = menuItems?.map((item, index) => {
    return item.variations?.length === 1
      ? { itemKey: item.itemKey as string, variation: item.variations[0].name }
      : currentSelectedItems[index] ?? { itemKey: item.itemKey as string };
  });

  setSocialGiftBundleItems(selectedItems, menuItems);
};

export const isFullySpecified = (menuItems: MenuItem[]): boolean => {
  return menuItems.every((_, index) => {
    const selectedItems = get(sessionStore).socialGiftBundleSelectedItems;
    return selectedItems && selectedItems[index]?.variation;
  });
};

export const isMenuFullySpecified = (menuItems: MenuItem[]): boolean => {
  return menuItems.every((item) => item.variations?.length === 1);
};

type SelectedItem = { itemKey: string; variation: string };
type SelectedItemGroup = SelectedItem & {
  count: number;
  visibleVariation: string;
  visibleName: string;
  menuItem?: MenuItem;
};

export const groupBundleItems = (): SelectedItemGroup[] => {
  // Assuming that, when using this method, the items are already fully specified
  const selectedItems = get(sessionStore).socialGiftBundleSelectedItems as SelectedItem[];
  const menuItems = get(sessionStore).socialGiftBundleMenuItems as MenuItem[];

  const menuItemsMap = keyBy(menuItems, "itemKey");

  return selectedItems?.reduce((acc, item) => {
    const groupedItem = acc.find((group) => group.itemKey === item.itemKey && group.variation === item.variation);

    if (groupedItem) {
      groupedItem.count++;
    } else {
      const menuItem: MenuItem | undefined = menuItemsMap[item.itemKey];

      acc.push({
        ...item,
        count: 1,
        visibleVariation:
          menuItem?.variations?.find((variation) => variation.name === item.variation)?.visible_name ?? item.variation,
        visibleName: (menuItem?.visible_name as string | undefined) ?? item.itemKey?.split("|")[0],
        menuItem
      });
    }

    return acc;
  }, [] as SelectedItemGroup[]);
};

export type MultiSelectVisibleData = { visibleName: string; visibleVariation: string; visibleVariation2?: string };

export const groupMultipleSelectionItems = (availableMenuItems: AvailableMenuItem[]): MultiSelectVisibleData[] => {
  return (get(sessionStore).multipleSelectionSelectedItems ?? [])
    .map(({ item_key: itemKey, variation_key: variation }) => {
      const matchedItem = availableMenuItems.find(
        (item) => item.item_key === itemKey && item.variation_key === variation
      );

      return (
        matchedItem && {
          visibleName: matchedItem.visible_name,
          visibleVariation: matchedItem.visible_variation,
          visibleVariation2: matchedItem.visible_variation_2
        }
      );
    })
    .filter(Boolean) as MultiSelectVisibleData[];
};

export function formatMultiSelectionVariations(item: MultiSelectVisibleData) {
  if (item.visibleVariation === "default") return "";
  return `(${[item.visibleVariation, item.visibleVariation2].filter(Boolean).join(", ")})`;
}
